// Partner theming layer — partners are CONFIGURATION, not builds.
// Default is always FHO's own brand. A partner theme activates only via
// ?partner=<id> in the URL (then persists for the session via localStorage).
// Nothing links to partner modes publicly; they exist for pilots and demos.

const THEMES = {
  fho: {
    id: 'fho',
    partnerName: null,            // null = FHO's own brand, no co-brand header
    poweredByFHO: true,           // co-branded — shows the FHO mark
    colors: {
      primary: '#1e3a5f',         // header / buttons / ink
      accent: '#4f46e5',
      pageBg: '#5b9bd5',
      headerText: '#ffffff',
    },
    escalation: null,             // no escalation card in default mode (yet)
  },

  mchap: {
    id: 'mchap',
    partnerName: 'Marketplace Chaplains',
    partnerSub: 'Employee Care',
    logo: '/partners/marketplace-chaplains.png',
    logoWidth: 190,
    logoPlate: true,   // logo file has an opaque white background
    coachLabel: 'Care Companion',
    poweredByFHO: true,
    colors: {
      primary: '#0d2b5f',         // approximation — replace with brand kit on partnership
      accent: '#e8a33d',
      userBubble: '#1e4278',
      pageBg: '#071b3d',
      headerText: '#ffffff',
    },
    escalation: {
      title: 'Connect with your Chaplain Care Team',
      body: 'Your chaplain visits your workplace and can meet privately — this conversation stays confidential.',
      button: 'Request a visit with my chaplain',
      consentLabel: 'Share a short summary of this conversation with my chaplain so I don\u2019t have to start over. Nothing is shared unless you choose.',
    },
  },

  rfc: {
    id: 'rfc',
    partnerName: 'Real Fellas Chaplains',
    partnerSub: 'Chaplain Care',
    logo: '/partners/real-fellas-chaplains-header.png',
    logoWidth: 210,
    poweredByFHO: true,           // co-branded — shows the FHO mark
    coachLabel: 'Faith Coach',
    home: '/emit-the-bible',      // this partner runs on Emit only
    colors: {
      primary: '#121117',         // sampled from the logo background so it blends
      accent: '#c8cdd4',
      pageBg: '#08080a',
      userBubble: '#2b2b31',
      headerText: '#ffffff',
    },
    escalation: {
      title: 'Connect with a chaplain',
      live: true,
      body: 'A Real Fellas chaplain can talk with you privately. This is not an emergency service.',
      button: 'Request a call with a chaplain',
      consentLabel: 'Share a short summary of this conversation with the chaplain so I don\u2019t have to start over. Nothing is shared unless you choose.',
    },
  },

  hero: {
    id: 'hero',
    partnerName: 'Veteran Wellbeing Pilot',
    partnerSub: 'Pilot Preview \u00b7 Ready for Partner Testing',
    coachLabel: 'Wellness Coach',
    home: '/safe',                // this pilot runs on SAFE only
    crisisLine: 'In crisis? Veterans Crisis Line: dial 988 then press 1, or text 838255. In immediate danger, call 911.',
    welcome: {
      lead: 'A steady place to talk things through, at any hour.',
      note: 'For service members, veterans, first responders, and their families. This is an AI wellness coach that works alongside your own providers, never in place of them.',
      ask: 'Where would you like to start?',
      starters: [
        { title: 'Trouble sleeping', sub: 'Nights that will not settle', prompt: 'I have been having trouble sleeping.' },
        { title: 'Stress and anger', sub: 'When the pressure keeps building', prompt: 'I have been dealing with a lot of stress and anger.' },
        { title: 'Life after service', sub: 'Finding your footing again', prompt: 'I am trying to find my footing after service.' },
        { title: 'Family and relationships', sub: 'The people closest to you', prompt: 'I want to talk about my family and relationships.' },
        { title: 'Supporting someone who served', sub: 'For spouses, parents and partners', prompt: 'I am supporting someone who served, and I could use some help.' },
        { title: 'I just need to talk', sub: 'No agenda needed', prompt: 'I just need to talk.' },
      ],
    },
    poweredByFHO: true,
    colors: {
      primary: '#16263a',
      accent: '#9fb8d3',
      pageBg: '#0a121d',
      userBubble: '#24384f',
      headerText: '#ffffff',
    },
    escalation: null,             // no hand-off until a destination is chosen with the partner
  },

  safe: {
    id: 'safe',
    partnerName: 'SAFE',
    partnerSub: 'Your Safe Space for Growth',
    coachLabel: 'Wellness Coach',
    home: '/safe',                // stand-alone SAFE: its access code keeps the account on SAFE
    standard: true,               // standard SAFE inside: its colors, voice default and setup wording
    schemes: ['sage', 'slate', 'rose'],
    welcome: {
      lead: "I'm SAFE, your mental health buddy. This is a judgment-free space where you can share whatever's on your mind.",
      note: 'Together, we can help you experience greater Freedom, Happiness, and Chosen Oneness.',
      ask: 'Where would you like to start?',
      starters: [
        { title: 'Stress and anxiety', sub: "When your mind won't slow down", prompt: "I've been feeling stressed and anxious." },
        { title: 'Trouble sleeping', sub: 'Nights that will not settle', prompt: "I've been having trouble sleeping." },
        { title: 'Feeling down', sub: 'When the days feel heavy', prompt: "I've been feeling down lately." },
        { title: 'Relationships', sub: 'The people closest to you', prompt: 'I want to talk about a relationship in my life.' },
        { title: 'Grief and loss', sub: 'Carrying someone you miss', prompt: "I'm dealing with grief and loss." },
        { title: 'I just need to talk', sub: 'No agenda needed', prompt: 'I just need to talk.' },
      ],
    },
    poweredByFHO: true,
    colors: {
      primary: '#352F29',
      accent: '#C4A77D',
      pageBg: '#2A2520',
      userBubble: '#526F57',
      headerText: '#ffffff',
    },
    escalation: null,
  },

  chaplain: {
    id: 'chaplain',
    partnerName: 'Chaplain Care',
    partnerSub: 'Faith & Spiritual Coach',
    coachLabel: 'Faith Coach',
    coachLabels: { emit: 'Faith Coach', sage: 'Spiritual Coach' },
    home: '/emit-the-bible',      // stand-alone Chaplain app: its access code keeps the account on Emit
    noLinks: true,
    builtBy: true,
    voices: true,                 // voice choice in the menu (same voices as SAFE)
    poweredByFHO: true,
    colors: {
      primary: '#1f2a44',
      accent: '#c9a86a',
      pageBg: '#0f1626',
      userBubble: '#2b3a5c',
      headerText: '#ffffff',
    },
    welcome: {
      lead: 'A quiet place to talk, any hour. A chaplain is here for you when you want a person.',
      starters: [
        { title: 'Something heavy on my heart', sub: 'Say it in your own words', prompt: 'There is something heavy on my heart.' },
        { title: 'Stress at work', sub: 'When the pressure keeps building', prompt: "I've been dealing with a lot of stress at work." },
        { title: 'Family and relationships', sub: 'The people closest to you', prompt: 'I want to talk about my family and relationships.' },
        { title: 'Grief and loss', sub: 'Carrying someone you miss', prompt: "I'm dealing with grief and loss." },
        { title: 'Questions about faith', sub: 'Doubts, prayer, and where God is', prompt: 'I have some questions about faith.' },
        { title: "I'd like to talk to a chaplain", sub: 'Ask for a call with a person', prompt: "I'd like to talk to a chaplain." },
      ],
    },
    escalation: {
      title: 'Connect with a chaplain',
      live: true,
      body: 'A chaplain can talk with you privately. This is not an emergency service.',
      button: 'Request a call with a chaplain',
      consentLabel: 'Share a short summary of this conversation with the chaplain so I don\u2019t have to start over. Nothing is shared unless you choose.',
    },
  },


  faith: {
    id: 'faith',
    partnerName: 'Faith & Spiritual',
    partnerSub: 'Faith & Spiritual Coach',
    coachLabel: 'Faith Coach',
    coachLabels: { emit: 'Faith Coach', sage: 'Spiritual Coach' },
    home: '/emit-the-bible',      // stand-alone Faith & Spiritual app: its access code keeps the account on Emit
    noLinks: true,
    builtBy: true,
    voices: true,                 // voice choice in the menu (same voices as SAFE)
    poweredByFHO: true,
    colors: {
      primary: '#1f2a44',
      accent: '#c9a86a',
      pageBg: '#0f1626',
      userBubble: '#2b3a5c',
      headerText: '#ffffff',
    },
    welcome: {
      lead: 'A quiet place to talk, any hour.',
      starters: [
        { title: 'Something heavy on my heart', sub: 'Say it in your own words', prompt: 'There is something heavy on my heart.' },
        { title: 'Stress at work', sub: 'When the pressure keeps building', prompt: "I've been dealing with a lot of stress at work." },
        { title: 'Family and relationships', sub: 'The people closest to you', prompt: 'I want to talk about my family and relationships.' },
        { title: 'Grief and loss', sub: 'Carrying someone you miss', prompt: "I'm dealing with grief and loss." },
        { title: 'Questions about faith', sub: 'Doubts, prayer, and where God is', prompt: 'I have some questions about faith.' },
        { title: 'Finding peace', sub: "When your mind won't rest", prompt: "I can't seem to find peace right now." },
      ],
    },
    escalation: null,             // no hand-off card in this version (access code FAITHAPP01)
  },
  mp: {
    id: 'mp',
    partnerName: 'Mental Performance',
    partnerSub: 'Mental Performance Coach',
    home: '/mental-performance',  // stand-alone Mental Performance app: its access code keeps the account here
    noLinks: true,
    builtBy: true,
    voices: true,
    schemes: ['midnight', 'graphite', 'plum'],
    colors: {
      primary: '#1A1D2E',
      accent: '#0EA5E9',
      pageBg: '#1A1D2E',
      userBubble: '#252A3D',
      headerText: '#ffffff',
    },
  },

  family: {
    id: 'family',
    partnerName: 'Family Coach',
    partnerSub: 'Better Than a Parent',
    home: '/family-coach',        // stand-alone Family Coach app: its access code keeps the account here
    noLinks: true,
    builtBy: true,
    voices: true,
    schemes: ['seafoam', 'sand', 'lavender'],
    colors: {
      primary: '#264653',
      accent: '#2A9D8F',
      pageBg: '#264653',
      userBubble: '#2A9D8F',
      headerText: '#ffffff',
    },
  },

  arc: {
    id: 'arc',
    partnerName: 'ARC',
    partnerSub: 'Alliance for Responsible Citizenship',
    coachLabel: 'ARC Companion',
    poweredByFHO: true,
    colors: {
      primary: '#14213d',         // placeholder for demo — replace from their logo / brand kit
      accent: '#c9a86a',
      pageBg: '#0b1426',
      userBubble: '#22335a',
      headerText: '#ffffff',
    },
    escalation: null,
  },
};

export function getPartnerTheme() {
  // Activated by ?partner=<id>, then held in sessionStorage so it survives
  // navigation between coaches within the same tab. Dies when the tab closes.
  // NOT localStorage — that persisted across devices and days and leaked
  // partner branding onto the plain /emit-the-bible URL.
  try { localStorage.removeItem('fho_partner'); } catch (e) {}
  // An account that belongs to a pilot stays in it, whatever link was used.
  try {
    const locked = sessionStorage.getItem('fho_account_partner');
    if (locked && THEMES[locked]) return THEMES[locked];
  } catch (e) { /* storage-blocked contexts */ }
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('partner');
    if (fromUrl && THEMES[fromUrl]) {
      if (fromUrl === 'fho') sessionStorage.removeItem('fho_partner');
      else sessionStorage.setItem('fho_partner', fromUrl);
      return THEMES[fromUrl];
    }
    const stored = sessionStorage.getItem('fho_partner');
    if (stored && THEMES[stored]) return THEMES[stored];
  } catch (e) { /* storage-blocked contexts fall through to default */ }
  return THEMES.fho;
}

// The active theme when it is a single-coach pilot (has `home`), else null.
export function getPilotTheme() {
  const active = getPartnerTheme();
  return active && active.home ? active : null;
}

export function clearPartnerTheme() {
  try { localStorage.removeItem('fho_partner'); } catch (e) {}
}

// Ask the server whether this account belongs to a pilot and remember the
// answer for this tab. Resolves true when the answer changed.
export async function syncAccountPartner() {
  try {
    const token = localStorage.getItem('token');
    const before = sessionStorage.getItem('fho_account_partner') || '';
    if (!token) { sessionStorage.removeItem('fho_account_partner'); return before !== ''; }
    const res = await fetch('/api/v1/journey/account-partner', { headers: { Authorization: 'Bearer ' + token } });
    if (!res.ok) return false;
    const data = await res.json();
    const now = (data && data.theme && THEMES[data.theme]) ? data.theme : '';
    if (now) sessionStorage.setItem('fho_account_partner', now);
    else sessionStorage.removeItem('fho_account_partner');
    return before !== now;
  } catch (e) { return false; }
}

// A partner whose pilot runs on one coach sets `home`. Anyone who lands on
// another coach's page in that mode is sent to the pilot's coach instead.
const COACH_PAGES = ['/emit-the-bible', '/safe', '/family-coach', '/mental-performance', '/select-home-app'];

// Where this visitor should be instead of `path`, or null if they may stay.
export function partnerHomeFor(path) {
  try {
    const active = getPartnerTheme();
    const under = (base) => path === base || path.startsWith(base + '/');
    if (active && active.home && COACH_PAGES.some(under) && !under(active.home)) return active.home;
  } catch (e) { /* storage-blocked contexts */ }
  return null;
}

// On a fresh page load, before the app starts. Page changes inside the app
// are covered by components/PartnerHomeGuard.jsx.
try {
  const target = partnerHomeFor(window.location.pathname);
  if (target) window.location.replace(target);
} catch (e) { /* non-browser contexts */ }

export default THEMES;
